import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import Hoc from "../../component/HOC";
import FormInput from "../ProgramSubscriptions/FormInput";
import RichTextEditor from "../ProgramSubscriptions/TextEditor";
import "../ProgramSubscriptions/Program.scss";
import {
  getGuestPurchaseEmailTemplates,
  saveGuestPurchaseEmailTemplate,
  updateGuestPurchaseEmailTemplate,
  showDetail,
} from "../../redux/action/user";

const decodeHtmlEntitiesToHtml = (input) => {
  const str = String(input ?? "");
  if (!str) return "";
  if (!str.includes("&lt;") && !str.includes("&gt;") && !str.includes("&amp;"))
    return str;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

const defaultProgramForm = () => ({
  id: "",
  emailSubject: "",
  bodyHtml: "",
  showDownloadLink: false,
});

const defaultSubscriptionForm = () => ({
  id: "",
  emailSubject: "",
  bodyHtml: "",
  showDownloadLink: true,
});

const normalizeTemplateRow = (d, defaults) => {
  if (!d || typeof d !== "object") {
    return { ...defaults };
  }
  const defShow = defaults.showDownloadLink;
  const rawShow = d.show_download_link ?? d.showDownloadLink;
  const showDownloadLink =
    rawShow === undefined || rawShow === null
      ? defShow
      : String(rawShow) === "1" || rawShow === true;
  return {
    id: String(d.id ?? d.ID ?? ""),
    emailSubject: d.email_subject ?? d.emailSubject ?? "",
    bodyHtml: decodeHtmlEntitiesToHtml(d.body_html ?? d.bodyHtml ?? ""),
    showDownloadLink,
  };
};

const MergeTagsReference = ({ mergeTags, loading }) => {
  const tags = mergeTags ?? [];

  return (
    <Box
      sx={{
        px: 1,
        py: 1.5,
        mb: 1,
        borderRadius: 1,
        border: "1px solid rgb(39, 51, 52)",
        backgroundColor: "rgb(22, 34, 36)",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: "#0bd7ee", mb: 0.5, textTransform: "uppercase", fontSize: "0.75rem" }}
      >
        Available merge tags
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mb: 1 }}>
        Insert these tags in the subject or body. The server replaces them when the email is sent.
      </Typography>
      {loading && tags.length === 0 ? (
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
          Loading merge tags…
        </Typography>
      ) : tags.length === 0 ? (
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)" }}>
          No merge tags returned from the server.
        </Typography>
      ) : (
        <Box
          component="ul"
          sx={{
            m: 0,
            pl: 2.5,
            maxHeight: 220,
            overflowY: "auto",
            "& li": { mb: 0.75 },
          }}
        >
          {tags.map((row) => (
            <Box component="li" key={row.tag}>
              <Typography
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  color: "#fff",
                  fontWeight: 600,
                }}
              >
                {row.tag}
              </Typography>
              {row.description ? (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.7)", ml: 1 }}
                >
                  — {row.description}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const GuestPurchaseEmailTemplatesForm = (props) => {
  const [tab, setTab] = useState(0);
  const [programForm, setProgramForm] = useState(defaultProgramForm);
  const [subscriptionForm, setSubscriptionForm] = useState(defaultSubscriptionForm);

  useEffect(() => {
    props.showDetail(true);
    props.getGuestPurchaseEmailTemplates();
    return () => props.showDetail(false);
  }, []);

  useEffect(() => {
    const bundle = props.guestPurchaseEmailTemplates;
    if (!bundle) return;
    setProgramForm(normalizeTemplateRow(bundle.program, defaultProgramForm()));
    setSubscriptionForm(
      normalizeTemplateRow(bundle.subscription, defaultSubscriptionForm())
    );
  }, [props.guestPurchaseEmailTemplates]);

  const handleProgramField = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setProgramForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubscriptionField = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setSubscriptionForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleProgramBody = useCallback((_, content) => {
    setProgramForm((prev) => ({ ...prev, bodyHtml: content }));
  }, []);

  const handleSubscriptionBody = useCallback((_, content) => {
    setSubscriptionForm((prev) => ({ ...prev, bodyHtml: content }));
  }, []);

  const buildPayload = (templateType, form, forceNoDownload) => {
    const payload = new URLSearchParams();
    payload.append("template_type", templateType);
    payload.append("email_subject", form.emailSubject || "");
    payload.append("body_html", form.bodyHtml || "");
    const showDl = forceNoDownload ? false : form.showDownloadLink;
    payload.append("show_download_link", showDl ? "1" : "0");
    return payload;
  };

  const submitProgram = async () => {
    if (!programForm.emailSubject?.trim()) {
      toast.error("Email subject is required");
      return;
    }
    const payload = buildPayload("program", programForm, true);
    const res = programForm.id
      ? await props.updateGuestPurchaseEmailTemplate(
          (() => {
            const p2 = new URLSearchParams(payload);
            p2.append("id", programForm.id);
            return p2;
          })()
        )
      : await props.saveGuestPurchaseEmailTemplate(payload);
    if (res && res.status === 200) {
      if (res.data?.message) toast.success(res.data.message);
      else toast.success("Program purchase email saved");
      props.getGuestPurchaseEmailTemplates();
    } else {
      toast.error(
        res?.response?.data?.message || res?.message || "Something went wrong"
      );
    }
  };

  const submitSubscription = async () => {
    if (!subscriptionForm.emailSubject?.trim()) {
      toast.error("Email subject is required");
      return;
    }
    const payload = buildPayload("subscription", subscriptionForm, false);
    const res = subscriptionForm.id
      ? await props.updateGuestPurchaseEmailTemplate(
          (() => {
            const p2 = new URLSearchParams(payload);
            p2.append("id", subscriptionForm.id);
            return p2;
          })()
        )
      : await props.saveGuestPurchaseEmailTemplate(payload);
    if (res && res.status === 200) {
      if (res.data?.message) toast.success(res.data.message);
      else toast.success("Subscription purchase email saved");
      props.getGuestPurchaseEmailTemplates();
    } else {
      toast.error(
        res?.response?.data?.message || res?.message || "Something went wrong"
      );
    }
  };

  return (
    <Box sx={pageWrap}>
      <Hoc activeMenu="Email Templates" subTitle="Guest checkout">
        <Box className="container-fluid p-0 mt-4" sx={panelWrap}>
          <Box sx={{ px: 2, pt: 2 }}>
            <Link
              to="/email-templates"
              style={{ color: "#0bd7ee", textDecoration: "none", fontSize: "0.9rem" }}
            >
              ← Back to all email templates
            </Link>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Program purchase" />
              <Tab label="Subscription purchase" />
            </Tabs>
          </Box>

          <Grid container spacing={2} className="FieldsWrap" sx={{ px: 1 }}>
            <Grid item xs={12} md={12}>
              <Box sx={{ px: 1, py: 1 }}>
                <MergeTagsReference
                  mergeTags={props.guestPurchaseEmailTemplates?.mergeTags}
                  loading={props.guestPurchaseEmailTemplatesLoading}
                />
              </Box>

              <div style={{ display: tab === 0 ? "block" : "none" }}>
                <FormInput
                  label="Email subject"
                  value={programForm.emailSubject}
                  onChange={handleProgramField("emailSubject")}
                  placeholder="e.g. 🎉 Program Purchased!"
                />
                <Box
                  sx={{
                    backgroundColor: "transparent",
                    padding: "8px",
                    borderBottom: "1px solid rgb(89 89 89 / 17%)",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "#fff",
                      width: "13%",
                      marginBottom: "8px",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Email body:
                  </label>
                  <Box sx={{ width: "100%", backgroundColor: "#fff", borderRadius: 1 }}>
                    <RichTextEditor
                      index={0}
                      value={programForm.bodyHtml}
                      onChange={handleProgramBody}
                      rawHtml
                    />
                  </Box>
                </Box>
                <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={submitProgram}
                    disabled={
                      props.saveGuestPurchaseEmailTemplateLoading ||
                      props.guestPurchaseEmailTemplatesLoading
                    }
                    sx={{
                      mt: 1,
                      backgroundColor: "#0bd7ee",
                      color: "#000",
                      textTransform: "none",
                    }}
                  >
                    Save program template
                  </Button>
                </Box>
              </div>

              <div style={{ display: tab === 1 ? "block" : "none" }}>
                <FormInput
                  label="Email subject"
                  value={subscriptionForm.emailSubject}
                  onChange={handleSubscriptionField("emailSubject")}
                  placeholder="e.g. 🎉 Subscription Purchased!"
                />
                <Box
                  sx={{
                    backgroundColor: "transparent",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgb(89 89 89 / 17%)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      width: "13%",
                      marginRight: "8px",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Options:
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={subscriptionForm.showDownloadLink}
                        onChange={(e) =>
                          setSubscriptionForm((prev) => ({
                            ...prev,
                            showDownloadLink: e.target.checked,
                          }))
                        }
                        color="primary"
                        sx={{
                          color: "#fff",
                          "& .MuiSvgIcon-root": {
                            border: "1px solid #000",
                            borderRadius: "2px",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ color: "#fff", fontSize: "0.9rem" }}>
                        Show “Download Receipt” link (uses {"{{download_url}}"} in body)
                      </Typography>
                    }
                  />
                </Box>
                <Box
                  sx={{
                    backgroundColor: "transparent",
                    padding: "8px",
                    borderBottom: "1px solid rgb(89 89 89 / 17%)",
                  }}
                >
                  <label
                    style={{
                      display: "block",
                      color: "#fff",
                      width: "13%",
                      marginBottom: "8px",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Email body:
                  </label>
                  <Box sx={{ width: "100%", backgroundColor: "#fff", borderRadius: 1 }}>
                    <RichTextEditor
                      index={1}
                      value={subscriptionForm.bodyHtml}
                      onChange={handleSubscriptionBody}
                      rawHtml
                    />
                  </Box>
                </Box>
                <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={submitSubscription}
                    disabled={
                      props.saveGuestPurchaseEmailTemplateLoading ||
                      props.guestPurchaseEmailTemplatesLoading
                    }
                    sx={{
                      mt: 1,
                      backgroundColor: "#0bd7ee",
                      color: "#000",
                      textTransform: "none",
                    }}
                  >
                    Save subscription template
                  </Button>
                </Box>
              </div>
            </Grid>
          </Grid>
        </Box>
      </Hoc>
    </Box>
  );
};

const pageWrap = {
  ".menu-item-container": {
    "& p": {
      color: "#fff",
      opacity: 1,
    },
  },
};

const panelWrap = {
  boxShadow:
    "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
  width: "100%",
  overflowX: "auto",
  backgroundColor: "rgb(15, 28, 30)",
  border: "1px solid rgb(39, 51, 52)",
  color: "rgb(255, 255, 255)",
  margin: "10px 20px",
};

const actionCreators = {
  showDetail,
  getGuestPurchaseEmailTemplates,
  saveGuestPurchaseEmailTemplate,
  updateGuestPurchaseEmailTemplate,
};

export default connect(
  ({ user }) => ({
    guestPurchaseEmailTemplates: user.guestPurchaseEmailTemplates,
    guestPurchaseEmailTemplatesLoading: user.guestPurchaseEmailTemplatesLoading,
    saveGuestPurchaseEmailTemplateLoading: user.saveGuestPurchaseEmailTemplateLoading,
  }),
  actionCreators
)(GuestPurchaseEmailTemplatesForm);
